import React, { createContext, useContext, useState, ReactNode } from 'react';
import { AppConfig, Layer, GeneratedImage, BlendMode } from '../types';
import { UniqueNFT } from '../types/index';

// Default configuration for the application
const defaultConfig: AppConfig = {
  namePrefix: "NFT Art Engine Collection",
  description: "A collection of unique NFT artwork generated with NFT Art Engine",
  baseURI: "", // Empty by default, will be optional
  format: {
    width: 512,
    height: 512,
    smoothing: false
  },
  background: {
    generate: false,
    brightness: "80%",
    static: false,
    default: "#000000"
  },
  extraMetadata: {},
  rarityDelimiter: "#",
  uniqueDnaTorrance: 10000,
  layerConfigurations: [
    {
      growEditionSizeTo: 10,
      layersOrder: []
    }
  ]
};

// Context interface
interface AppContextType {
  currentStep: number;
  setCurrentStep: (step: number) => void;
  config: AppConfig;
  updateConfig: (updates: Partial<AppConfig>) => void;
  layers: Layer[];
  setLayers: (layers: Layer[]) => void;
  addLayer: (name: string, blend: BlendMode) => void;
  removeLayer: (id: number) => void;
  updateLayer: (id: number, updates: Partial<Layer>) => void;
  generatedImages: GeneratedImage[];
  setGeneratedImages: (images: GeneratedImage[]) => void;
  isGenerating: boolean;
  setIsGenerating: (isGenerating: boolean) => void;
  uniqueNFTs: UniqueNFT[];
  setUniqueNFTs: (nfts: UniqueNFT[]) => void;
  addUniqueNFT: (nft: UniqueNFT) => void;
  removeUniqueNFT: (id: number) => void;
  updateUniqueNFT: (id: number, updates: Partial<UniqueNFT>) => void;
}

// Create context with default values
const AppContext = createContext<AppContextType>({
  currentStep: 0,
  setCurrentStep: () => {},
  config: defaultConfig,
  updateConfig: () => {},
  layers: [],
  setLayers: () => {},
  addLayer: () => {},
  removeLayer: () => {},
  updateLayer: () => {},
  generatedImages: [],
  setGeneratedImages: () => {},
  isGenerating: false,
  setIsGenerating: () => {},
  uniqueNFTs: [],
  setUniqueNFTs: () => {},
  addUniqueNFT: () => {},
  removeUniqueNFT: () => {},
  updateUniqueNFT: () => {},
});

// Provider component
interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [config, setConfig] = useState<AppConfig>(defaultConfig);
  const [layers, setLayers] = useState<Layer[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [uniqueNFTs, setUniqueNFTs] = useState<UniqueNFT[]>([]);

  // Update config with partial updates
  const updateConfig = (updates: Partial<AppConfig>) => {
    setConfig(prevConfig => ({
      ...prevConfig,
      ...updates
    }));
  };

  // Add a new layer
  const addLayer = (name: string, blend: BlendMode = 'source-over') => {
    const newLayer: Layer = {
      id: Date.now(),
      name,
      blend,
      opacity: 1,
      elements: []
    };
    setLayers([...layers, newLayer]);
  };

  // Remove a layer by id
  const removeLayer = (id: number) => {
    setLayers(layers.filter(layer => layer.id !== id));
  };

  // Update a layer with partial updates
  const updateLayer = (id: number, updates: Partial<Layer>) => {
    setLayers(layers.map(layer => 
      layer.id === id ? { ...layer, ...updates } : layer
    ));
  };

  // Add a unique NFT
  const addUniqueNFT = (nft: UniqueNFT) => {
    setUniqueNFTs([...uniqueNFTs, nft]);
  };

  // Remove a unique NFT
  const removeUniqueNFT = (id: number) => {
    setUniqueNFTs(uniqueNFTs.filter(nft => nft.id !== id));
  };

  // Update a unique NFT
  const updateUniqueNFT = (id: number, updates: Partial<UniqueNFT>) => {
    setUniqueNFTs(
      uniqueNFTs.map(nft => 
        nft.id === id ? { ...nft, ...updates } : nft
      )
    );
  };

  return (
    <AppContext.Provider
      value={{
        currentStep,
        setCurrentStep,
        config,
        updateConfig,
        layers,
        setLayers,
        addLayer,
        removeLayer,
        updateLayer,
        generatedImages,
        setGeneratedImages,
        isGenerating,
        setIsGenerating,
        uniqueNFTs,
        setUniqueNFTs,
        addUniqueNFT,
        removeUniqueNFT,
        updateUniqueNFT,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};

// Custom hook to use the app context
export const useAppContext = () => useContext(AppContext); 